import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import cn from 'classnames'
import { map, find } from 'lodash'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'

import Button from '../../components/Button'
import GratuityHeader from '../../components/GratuityHeader'

import Clock from '../../components/Clock'
import BackButton from '../../components/BackButton'

import NetworkIndicator from '../../containers/NetworkIndicator'
import CardReaderIndicator from '../../containers/CardReaderIndicator'

import { makeGetDigitalReceipt } from '../../selectors/digitalReceipt'
import { getReceiptDetails } from '../../actions/receipt'

import styles from './ReviewCheck.module.scss'
import stylesSignature from '../Signature.module.scss'

import { toFixed, centsToDollars } from '../../utils/formatters'
import { makeGetTabOrder } from '../../selectors/order'
import { useState } from 'react'

const ReviewCheck = ({ headerText, subHeaderText, disclaimer, payment, receiptLoading, groupedItems, secondaryItems, goBack, proceedToSignature, onMount, className }) => {
  const classNames = cn(styles.reviewCheck, className)
  const mediaQuery = window.matchMedia("(max-width: 800px)")

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(receiptLoading)
  }, [receiptLoading])

  useEffect(() => {
    onMount()
  }, [])

  const gratuityHeaderText = () => {
    if (mediaQuery.matches) {
      return (
        <span className={styles.orderTextWrapper}>
          <span className={styles.orderPayment}>{`Payment: ${payment}`}</span>
          <span className={styles.orderInfo}>{subHeaderText}</span>
        </span>
      )
    } else {
      return subHeaderText
    }
  }

  const formatDisplayQuantity = (quantity) => {
    if (!quantity) return
    let displayQuantity = toFixed(parseFloat(quantity), 2)
    return quantity >= 1 ? displayQuantity : `0${displayQuantity}`
  }

  const renderItems = (items) => {
    if (loading) {
      return (
        <ul className={styles.items}>
          <li className={styles.item}>
            <p className={cn(styles.top, styles.loading)}>
              <Skeleton height={22} width="100%" />
              <Skeleton height={22} width="100%" />
            </p>
          </li>
          <li className={styles.item}>
            <p className={cn(styles.top, styles.loading)}>
              <Skeleton height={22} width="100%" />
              <Skeleton height={22} width="100%" />
            </p>
          </li>
          <li className={styles.item}>
            <p className={cn(styles.top, styles.loading)}>
              <Skeleton height={22} width="100%" />
              <Skeleton height={22} width="100%" />
            </p>
          </li>
        </ul>
      )
    }

    return (
      <ul className={styles.items}>
        {map(items, (item, index) => {
          let modifiersElement = null

          if (item.modifiersString) {
            modifiersElement = <p className={styles.bottom}>{item.modifiersString}</p>
          }

          return (
            <li className={styles.item} key={index}>
              <p className={styles.top}>
                <span>{formatDisplayQuantity(item.quantity)} {item.name}</span>
                <span>{item.price}</span>
              </p>
              {modifiersElement}
            </li>
          )
        })}
      </ul>
    )
  }

  const makeMenuItemsDisplay = (lineItems) => {
    return map(lineItems, (items, menuType) => (
      <>
        {!loading && <div className={styles.menuType}>{menuType}</div>}
        {renderItems(items)}
      </>
    ))
  }

  return (
    <div className={classNames}>
      <SkeletonTheme color="#3c3e52" highlightColor="#52556f">
        <section className={stylesSignature.signatureNavbar}>
          <BackButton onClick={goBack} />
          {mediaQuery.matches && (
            <>
              <CardReaderIndicator className={stylesSignature.cardReader} />
              <NetworkIndicator className={stylesSignature.indicator} />
              <Clock className={stylesSignature.clock} />
            </>
          )}
        </section>
        <div className={styles.header}>
          <div className={styles.payment}>
            <span>Payment</span>
            <span>{payment}</span>
          </div>
        </div>
        <GratuityHeader
          className={cn(styles.gratuityHeader)}
          primaryText={loading ? <Skeleton width="15%" /> : headerText}
          secondaryText={gratuityHeaderText()}
        />
        <div className={styles.contentContainer}>
          {makeMenuItemsDisplay(groupedItems)}
          {renderItems(secondaryItems)}
          <p className={styles.disclaimer}>{disclaimer}</p>
          <div className={styles.buttonGroup}>
            <Button onClick={proceedToSignature} disabled={loading} className={styles.continueButton}>PROCEED TO SIGNATURE</Button>
          </div>
        </div>
      </SkeletonTheme>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const orderUuid = ownProps?.match?.params?.orderUuid ?? state?.order?.currentOrderId
  const paymentId = ownProps?.match?.params?.paymentId ?? ''
  const getTabOrder = makeGetTabOrder()
  const order = getTabOrder(state, orderUuid)
  const tenderPayment = find(order?.paymentInfo, (payment) => payment?.uuid === paymentId)
  const tenderAmountInCents = tenderPayment?.amountInCents
  const isFrozen = order?.frozen
  const getDigitalCheck = makeGetDigitalReceipt()
  const digitalCheck = getDigitalCheck(state, orderUuid)
  const { total, secondaryItems, customerOrderString, loading, payment, ...rest } = digitalCheck

  return {
    headerText: tenderAmountInCents ? centsToDollars(tenderAmountInCents) : total,
    subHeaderText: tenderPayment ? `${customerOrderString} - Total: ${total}` : customerOrderString,
    ...rest,
    receiptLoading: loading,
    isFrozen,
    payment: tenderPayment?.shortDescription || payment,
    secondaryItems,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const orderUuid = ownProps?.match?.params?.orderUuid ?? ''
  const paymentId = ownProps?.match?.params?.paymentId ?? ''
  const replace = ownProps?.history?.replace ?? function(){}
  const push = ownProps?.history?.push ?? function(){}

  return {
    goBack: () => {
      replace(`/tabs/${orderUuid}/close/actions`)
    },
    proceedToSignature: () => {
      if (paymentId) {
        push(`/signature/${orderUuid}/${paymentId}?action=close-tender`)
      } else {
        push(`/signature/${orderUuid}?action=close`)
      }
    },
    onMount: () => {
      dispatch(getReceiptDetails({ orderId: orderUuid }))
    }
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ReviewCheck))
